import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { ScheduleJobCard } from './ScheduleJobCard';
import {
  dropId,
  jobWindow,
  packRows,
  type ScheduleJob,
  type ScheduleLane,
} from './scheduleModel';

export const HOUR_PX = 88;
const ROW_PX = 60;
const NAME_PX = 168;
const ANYTIME_PX = 128;

interface DayTimelineProps {
  lanes: ScheduleLane[];
  jobsByLane: Map<string, ScheduleJob[]>;
  hours: { from: number; to: number };
  /** Minutes since midnight in the company timezone when the day shown is today. */
  nowMinutes: number | null;
  timezone: string;
  dragging: boolean;
  selectedJobId: string | null;
  onOpenJob: (id: string) => void;
}

export function DayTimeline({
  lanes, jobsByLane, hours, nowMinutes, timezone, dragging, selectedJobId, onOpenJob,
}: DayTimelineProps) {
  const hourCount = hours.to - hours.from;
  const trackWidth = hourCount * HOUR_PX;

  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <div style={{ minWidth: NAME_PX + ANYTIME_PX + trackWidth }}>
        {/* Hour ruler */}
        <div className="sticky top-0 z-30 flex border-b bg-card text-xs text-muted-foreground">
          <div className="sticky left-0 z-10 shrink-0 border-r bg-card px-3 py-2 font-medium" style={{ width: NAME_PX }}>
            Technician
          </div>
          <div className="shrink-0 border-r px-2 py-2" style={{ width: ANYTIME_PX }}>Anytime</div>
          <div className="relative" style={{ width: trackWidth }}>
            {Array.from({ length: hourCount }, (_, i) => (
              <span key={i} className="absolute top-2 pl-1.5" style={{ left: i * HOUR_PX }}>
                {formatHour(hours.from + i)}
              </span>
            ))}
          </div>
        </div>

        {lanes.map((lane) => (
          <DayLane
            key={lane.key}
            lane={lane}
            jobs={jobsByLane.get(lane.key) ?? []}
            hours={hours}
            trackWidth={trackWidth}
            nowMinutes={nowMinutes}
            timezone={timezone}
            dragging={dragging}
            selectedJobId={selectedJobId}
            onOpenJob={onOpenJob}
          />
        ))}
      </div>
    </div>
  );
}

interface DayLaneProps {
  lane: ScheduleLane;
  jobs: ScheduleJob[];
  hours: { from: number; to: number };
  trackWidth: number;
  nowMinutes: number | null;
  timezone: string;
  dragging: boolean;
  selectedJobId: string | null;
  onOpenJob: (id: string) => void;
}

function DayLane({ lane, jobs, hours, trackWidth, nowMinutes, timezone, dragging, selectedJobId, onOpenJob }: DayLaneProps) {
  const timeline = useDroppable({ id: dropId({ view: 'day', laneKey: lane.key, zone: 'timeline' }) });
  const anytime = useDroppable({ id: dropId({ view: 'day', laneKey: lane.key, zone: 'anytime' }) });

  const timed: { job: ScheduleJob; start: number; end: number }[] = [];
  const untimed: ScheduleJob[] = [];
  for (const job of jobs) {
    const w = jobWindow(job);
    if (w) timed.push({ job, start: w.start, end: w.end });
    else untimed.push(job);
  }
  const packed = packRows(timed);
  const rowCount = Math.max(1, ...packed.map((p) => p.row + 1));
  const height = Math.max(rowCount * ROW_PX + 8, untimed.length * 44 + 8, ROW_PX + 8);
  const originMin = hours.from * 60;
  const isUnassigned = lane.techId === null;

  return (
    <div className={cn('flex border-b last:border-b-0', isUnassigned && 'bg-muted/40')}>
      <div
        className={cn('sticky left-0 z-20 flex shrink-0 items-start gap-2 border-r px-3 py-2', isUnassigned ? 'bg-muted' : 'bg-card')}
        style={{ width: NAME_PX }}
      >
        <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: lane.colour }} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">{lane.name}</span>
          <span className="block text-xs text-muted-foreground">
            {jobs.length === 0 ? 'Free' : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`}
            {lane.inactive ? ' · inactive' : ''}
          </span>
        </span>
      </div>

      <div
        ref={anytime.setNodeRef}
        className={cn(
          'flex shrink-0 flex-col gap-1 border-r p-1 transition-colors',
          dragging && 'bg-muted/30',
          anytime.isOver && 'bg-primary/10',
        )}
        style={{ width: ANYTIME_PX }}
      >
        {untimed.map((job) => (
          <ScheduleJobCard
            key={job.id}
            job={job}
            colour={lane.colour}
            timezone={timezone}
            compact
            selected={job.id === selectedJobId}
            onOpen={onOpenJob}
          />
        ))}
      </div>

      <div
        ref={timeline.setNodeRef}
        className={cn('relative shrink-0 transition-colors', timeline.isOver && 'bg-primary/5')}
        style={{
          width: trackWidth,
          height,
          backgroundImage: `linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px),
                            linear-gradient(to right, hsl(var(--border) / 0.45) 1px, transparent 1px)`,
          backgroundSize: `${HOUR_PX}px 100%, ${HOUR_PX / 2}px 100%`,
        }}
      >
        {nowMinutes !== null && nowMinutes >= originMin && nowMinutes <= hours.to * 60 && (
          <div
            className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-destructive"
            style={{ left: ((nowMinutes - originMin) / 60) * HOUR_PX }}
            aria-hidden
          />
        )}
        {packed.map(({ item, row }) => (
          <ScheduleJobCard
            key={item.job.id}
            job={item.job}
            colour={lane.colour}
            timezone={timezone}
            selected={item.job.id === selectedJobId}
            onOpen={onOpenJob}
            style={{
              position: 'absolute',
              top: row * ROW_PX + 4,
              height: ROW_PX - 6,
              left: ((item.start - originMin) / 60) * HOUR_PX + 2,
              width: Math.max(((item.end - item.start) / 60) * HOUR_PX - 4, 64),
            }}
          />
        ))}
      </div>
    </div>
  );
}

function formatHour(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}
